import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from "@mui/material/Tooltip";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";

const ListTable = ({
  data = [],
  config = [],
  sortField,
  sortOrder,
  onSort,
  onDelete,
  onRestore,
  selectedItems,
  onSelectItem,
  onSelectAll,
  actions = [],
  handlers = {},
  highlightedRowId = null, // ID строки для подсветки после возврата
  tableKey = "table", // Ключ таблицы для формирования id строк
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const navigate = useNavigate();

  const isAllSelected = data.length > 0 && data.every(row => selectedItems.includes(row.id));

  // Функция для получения красивого названия колонки
  const getColumnDisplayName = (column) => {
    // Специальные переводы для конкретных колонок
    const translations = {
      display_name: "Полное имя",
      contact_info: "Контакты",
      black_list: "Черный список",
      client_type: "Тип клиента",
      first_name: "Имя",
      last_name: "Фамилия",
      company_id: "Компания",
      // Новые переводы для полей активов
      asset_number: "Номер актива",
      name: "Название",
      status: "Статус",
      deposit: "Депозит",
      purchase_price: "Цена покупки",
      purchase_date: "Дата покупки",
      total_revenue: "Общий доход",
      rental_count: "Количество аренд",
      last_rental_date: "Дата последней аренды",
      profitability_date: "Дата окупаемости",
      maintenance_date: "Дата обслуживания",
      images: "Изображения",
      is_deleted: "Удален",
      deleted_at: "Дата удаления",
      specifications: "Спецификации",
      search_metadata: "Метаданные поиска",
      ai_tags: "AI теги",
      ai_updated_at: "Дата обновления AI",
      created_at: "Дата создания",
      updated_at: "Дата обновления",
      rental_point: "Пункт проката",
      rental_point_name: "Пункт проката",
      "rental_point.name": "Пункт проката",
      total_rental_amount: "Сумма аренд",
      total_deals_count: "Кол-во сделок",
    };

    // Если есть специальный перевод - используем его
    if (translations[column.field]) {
      return translations[column.field];
    }

    // Иначе используем label из базы данных
    return column.label;
  };

  const handleOpenMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setCurrentRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setCurrentRow(null);
  };

  const validActions = Array.isArray(actions) ? actions : []; // Если не массив, используем пустой массив

  return (
    <Box
      sx={{
        padding: 2,
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
        overflowX: "auto",
      }}
    >
      <Table>
        {/* Заголовок таблицы */}
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
            {/* Чекбокс для выбора всех строк */}
            <TableCell sx={{ fontWeight: 700 }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={
                  selectedItems.length > 0 && selectedItems.length < data.length
                }
                onChange={onSelectAll}
              />
            </TableCell>

            {/* Генерация заголовков из config */}
            {config.map((col) => (
              <TableCell
                key={col.field}
                sx={{
                  fontWeight: 700,
                  color: "#374151",
                  fontSize: "0.78rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  whiteSpace: "nowrap",
                }}
              >
                <Box display="flex" alignItems="center">
                  {getColumnDisplayName(col)}
                  {col.sortable && (
                    <IconButton onClick={() => onSort(col.field)} size="small">
                      {sortField === col.field && sortOrder === "asc" ? (
                        <ArrowDropUpIcon />
                      ) : (
                        <ArrowDropDownIcon />
                      )}
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            ))}

            <TableCell
              sx={{
                fontWeight: 700,
                color: "#374151",
                fontSize: "0.78rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Действия
            </TableCell>
          </TableRow>
        </TableHead>

        {/* Тело таблицы */}
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={row.id}
              id={`${tableKey}-row-${row.id}`}
              sx={{
                "&:nth-of-type(odd)": {
                  backgroundColor: row.deleted_at ? "#d3d3d3" : "#f9fafc",
                },
                "&:nth-of-type(even)": {
                  backgroundColor: row.deleted_at ? "#d3d3d3" : "#ffffff",
                },
                "&:hover": {
                  backgroundColor: row.deleted_at
                    ? "#c0c0c0"
                    : "#eef2ff !important",
                  cursor: "pointer",
                },
                opacity: row.deleted_at ? 0.5 : 1,
                transition: "background-color 0.15s ease",
                // Подсветка строки после возврата
                ...(highlightedRowId === row.id && {
                  animation: "highlightRow 2s ease-out",
                  "@keyframes highlightRow": {
                    "0%": { backgroundColor: "#fff3cd" },
                    "100%": { backgroundColor: "inherit" },
                  },
                }),
              }}
            >
              {/* Чекбокс для выбора строки */}
              <TableCell>
                <Checkbox
                  checked={selectedItems.includes(row.id)}
                  onChange={() => onSelectItem(row.id)}
                />
              </TableCell>

              {/* Остальные ячейки */}
              {config.map((col) => (
                <TableCell key={col.field}>
                  {(() => {
                    // Специальная обработка для клиента в аренде
                    if (
                      (col.field === "client.display_name" ||
                        col.field === "client_full_name") &&
                      (row.client || row.client_full_name)
                    ) {
                      const clientName =
                        row.client?.display_name ||
                        row.client?.name ||
                        row.client_full_name ||
                        "Не указан";
                      const clientId = row.client?.id || row.client_id;
                      const clientPhone =
                        row.client?.contact_info || row.client?.phone;

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Открыть карточку клиента">
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                if (clientId)
                                  navigate(`/clients/edit/${clientId}`);
                              }}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                padding: "6px 12px",
                                borderRadius: "20px",
                                border: "2px solid #2196F3",
                                backgroundColor: "#E3F2FD",
                                color: "#2196F3",
                                textDecoration: "none",
                                cursor: clientId ? "pointer" : "default",
                                transition: "all 0.3s ease",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                "&:hover": clientId
                                  ? {
                                      backgroundColor: "#BBDEFB",
                                      borderColor: "#1976D2",
                                      transform: "scale(1.05)",
                                    }
                                  : {},
                                "&:active": clientId
                                  ? {
                                      transform: "scale(0.98)",
                                    }
                                  : {},
                              }}
                            >
                              <PersonIcon sx={{ fontSize: "1rem" }} />
                              {clientName}
                            </Box>
                          </Tooltip>
                          {clientPhone && (
                            <Tooltip title="Позвонить">
                              <Box
                                component="a"
                                href={`tel:${clientPhone.replace(/\D/g, "")}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  padding: "4px 10px",
                                  borderRadius: "16px",
                                  border: "1px solid #4CAF50",
                                  backgroundColor: "#E8F5E9",
                                  color: "#4CAF50",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  fontSize: "0.8rem",
                                  "&:hover": {
                                    backgroundColor: "#C8E6C9",
                                    borderColor: "#388E3C",
                                    transform: "scale(1.05)",
                                  },
                                }}
                              >
                                <PhoneIcon sx={{ fontSize: "0.9rem" }} />
                                {clientPhone}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    }

                    // Специальная обработка для ассета в аренде
                    if (
                      (col.field === "asset.name" ||
                        col.field === "asset_names") &&
                      (row.asset || row.asset_names || row.rental_assets)
                    ) {
                      // Получаем первый ассет если есть список
                      const hasNoAssets = row.asset_names === "Нет активов";
                      const firstAsset = hasNoAssets ? null :
                        row.rental_assets?.[0]?.asset || row.asset;
                      const assetName =
                        firstAsset?.name || row.asset_names || "Не указан";
                      const assetId = hasNoAssets ? null :
                        firstAsset?.id || row.asset?.id || row.asset_id;

                      return (
                        <Tooltip
                          title={
                            assetId ? "Открыть карточку актива" : assetName
                          }
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              if (assetId) navigate(`/assets/edit/${assetId}`);
                            }}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              padding: "6px 12px",
                              borderRadius: "20px",
                              border: "2px solid #FF9800",
                              backgroundColor: "#FFF3E0",
                              color: "#F57C00",
                              textDecoration: "none",
                              cursor: assetId ? "pointer" : "default",
                              transition: "all 0.3s ease",
                              fontSize: "0.9rem",
                              fontWeight: 500,
                              "&:hover": assetId
                                ? {
                                    backgroundColor: "#FFE0B2",
                                    borderColor: "#EF6C00",
                                    transform: "scale(1.05)",
                                  }
                                : {},
                              "&:active": assetId
                                ? {
                                    transform: "scale(0.98)",
                                  }
                                : {},
                            }}
                          >
                            <InventoryIcon sx={{ fontSize: "1rem" }} />
                            {assetName}
                          </Box>
                        </Tooltip>
                      );
                    }

                    // Специальная обработка для телефонов
                    if (
                      (col.field === "phone" || col.field === "contact_info") &&
                      row[col.field]
                    ) {
                      const phoneValue = row[col.field];
                      // Проверяем что это телефон (начинается с + или содержит цифры)
                      if (/^[\d+\-\s()]*\d/.test(phoneValue)) {
                        return (
                          <Tooltip title="Позвонить">
                            <Box
                              component="a"
                              href={`tel:${phoneValue.replace(/\D/g, "")}`}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                padding: "6px 12px",
                                borderRadius: "20px",
                                border: "2px solid #2196F3",
                                backgroundColor: "#E3F2FD",
                                color: "#2196F3",
                                textDecoration: "none",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                "&:hover": {
                                  backgroundColor: "#BBDEFB",
                                  borderColor: "#1976D2",
                                  transform: "scale(1.05)",
                                },
                                "&:active": {
                                  transform: "scale(0.98)",
                                },
                              }}
                            >
                              <PhoneIcon sx={{ fontSize: "1rem" }} />
                              {phoneValue}
                            </Box>
                          </Tooltip>
                        );
                      }
                    }

                    // Специальная обработка для поля status
                    if (col.field === "status" && row.statusData) {
                      return (
                        <Chip
                          label={row.statusData.label}
                          color={row.statusData.color}
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: "8px" }}
                        />
                      );
                    }

                    // Пометка "Удалён" рядом с именем клиента
                    if (
                      (col.field === "display_name" || col.field === "name") &&
                      row.is_deleted
                    ) {
                      const nameValue =
                        row[col.field] || row.display_name || row.name || "—";
                      return (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <span
                            style={{
                              textDecoration: "line-through",
                              color: "#999",
                            }}
                          >
                            {nameValue}
                          </span>
                          <Chip
                            label="Удалён"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </Box>
                      );
                    }

                    // Поддержка вложенных полей типа "rental_point.name"
                    const fieldPath = col.field.split(".");
                    let value = row;

                    for (const key of fieldPath) {
                      value = value?.[key];
                      if (value === null || value === undefined) break;
                    }

                    // Проверяем что значение не является объектом (кроме null)
                    if (value !== null && value !== undefined) {
                      if (typeof value === "object") {
                        // Если это объект, пытаемся извлечь разумное строковое представление
                        if (value.name) {
                          return value.name;
                        } else if (value.title) {
                          return value.title;
                        } else {
                          // Если нет name/title, возвращаем строку вместо JSON.stringify
                          return "[объект]";
                        }
                      } else {
                        // Форматирование дат с временем для полей created_at, updated_at
                        if (
                          (col.field === "created_at" ||
                            col.field === "updated_at") &&
                          value &&
                          !isNaN(Date.parse(value))
                        ) {
                          return new Date(value).toLocaleString("ru-RU");
                        }
                        // Форматирование дат без времени для обычных дат
                        if (
                          (col.field.includes("date") ||
                            col.field.includes("_date") ||
                            col.field === "purchase_date" ||
                            col.field === "maintenance_due_date") &&
                          value &&
                          !isNaN(Date.parse(value))
                        ) {
                          return new Date(value).toLocaleDateString("ru-RU");
                        }
                        // Форматирование цен для полей с ценами
                        if (
                          (col.field.includes("price") ||
                            col.field === "deposit" ||
                            col.field.includes("amount") ||
                            col.field.includes("Amount") ||
                            col.field === "total_rental_amount") &&
                          !isNaN(value) &&
                          value !== ""
                        ) {
                          return `${parseFloat(value).toFixed(2)} ₽`;
                        }
                        // Специальная обработка для количества сделок
                        if (col.field === "total_deals_count") {
                          const count = parseInt(value) || 0;
                          return (
                            <Chip
                              label={count}
                              size="small"
                              color={
                                count > 5
                                  ? "success"
                                  : count > 0
                                    ? "primary"
                                    : "default"
                              }
                            />
                          );
                        }
                        // Специальная обработка для типа клиента
                        if (col.field === "gender") {
                          return { male: "Мужской", female: "Женский" }[value] || "—";
                        }
                        if (col.field === "client_type") {
                          const typeTranslations = {
                            individual: "Физическое лицо",
                            company: "Юридическое лицо",
                          };
                          return typeTranslations[value] || value;
                        }
                        // Если это примитивное значение, возвращаем как есть
                        return value;
                      }
                    }

                    return "—";
                  })()}
                </TableCell>
              ))}
              {/* Иконка "три точки" для действий */}
              <TableCell>
                <IconButton
                  onClick={(event) => handleOpenMenu(event, row)}
                  title="Действия"
                >
                  <MoreVertIcon />
                </IconButton>

                {/* Выпадающее меню */}
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor) && currentRow?.id === row.id}
                  onClose={handleCloseMenu}
                >
                  {validActions
                    .filter(
                      (action) => !action.condition || action.condition(row),
                    )
                    .map((action, index) => (
                      <Tooltip
                        key={index}
                        title={action.tooltip || ""}
                        arrow
                        placement="left" // Добавляем свойство для отображения подсказки слева
                      >
                        <MenuItem
                          onClick={() => {
                            action.onClick(currentRow.id, handlers);
                            handleCloseMenu();
                          }}
                          style={{ color: action.color || "inherit" }}
                        >
                          {action.label}
                        </MenuItem>
                      </Tooltip>
                    ))}
                </Menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ListTable;
